import {
  FilterList as FilterListIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Alert, Box, Chip, CircularProgress, Stack, Tab, Tabs } from '@mui/material';
import { PermissionGate } from 'PermissionContext';
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Permissions } from '../../models/admin/Permission';
import { Palette } from 'Palette';
import { HEADING_SPACING } from 'DesignTokens';
import StyledSection from '../styles/StyledSection';
import { StyledFilterButton, StyledHeading } from '../styles/styles';
import ActivityLogEmptyState from './ActivityLogEmptyState';
import ActivityLogPageEntry from './ActivityLogPageEntry';
import ActivityLogPageFilterPanel from './ActivityLogPageFilterPanel';
import { useActivityLogPageList } from './useActivityLogPageList';

// Errors reported by the portals and the app: a separate tab, never mixed with activity logs, and
// only for admins with View Client Exceptions. Loaded only when opened.
const ClientExceptionsPanel = React.lazy(() => import('./clientExceptions/ClientExceptionsPanel'));

interface ActivityLogPageProps {
  tone?: keyof typeof Palette;
}

type ActivityLogTab = 'activity' | 'client-exceptions';

const ActivityLogPage: React.FC<ActivityLogPageProps> = ({ tone = 'default' }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryTab =
    searchParams.get('tab') === 'client-exceptions' ? 'client-exceptions' : 'activity';
  const queryGroupId = searchParams.get('group');
  const [tab, setTab] = useState<ActivityLogTab>(queryTab);
  const {
    logs,
    loading,
    loadingMore,
    error,
    totalLogs,
    showFilterPanel,
    setShowFilterPanel,
    filters,
    initiatedBySearchResults,
    associatedModelSearchResults,
    linkedModelSearchResults,
    initiatorTypeAnchorEl,
    setInitiatorTypeAnchorEl,
    associatedTypeAnchorEl,
    setAssociatedTypeAnchorEl,
    linkedTypeAnchorEl,
    setLinkedTypeAnchorEl,
    scrollRootRef,
    lastLogElementRef,
    backendService,
    handleFilterChange,
    handleGlobalSearchChange,
    clearFilters,
    refreshLogs,
  } = useActivityLogPageList();

  useEffect(() => {
    setTab(queryTab);
  }, [queryTab]);

  const updateSearchParams = useCallback(
    (nextTab: ActivityLogTab, groupId?: string | null) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (nextTab === 'client-exceptions') {
          next.set('tab', 'client-exceptions');
          if (groupId) {
            next.set('group', groupId);
          } else {
            next.delete('group');
          }
        } else {
          next.delete('tab');
          next.delete('group');
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, value: ActivityLogTab) => {
      setTab(value);
      updateSearchParams(value, value === 'client-exceptions' ? queryGroupId : null);
    },
    [queryGroupId, updateSearchParams],
  );

  const handleSelectedClientExceptionChange = useCallback(
    (groupId: string | null) => {
      updateSearchParams('client-exceptions', groupId);
    },
    [updateSearchParams],
  );

  const activity = (
    <>
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            <StyledHeading tone={tone} headerVariant="heading2" reduceBottomPadding>
              System Activity Logs
            </StyledHeading>
            {totalLogs > 0 && (
              <Chip label={totalLogs} size="small" sx={{ ml: 1 }} color="primary" />
            )}
          </Box>

          <Stack direction="row" spacing={1}>
            <StyledFilterButton
              active={showFilterPanel}
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              startIcon={<FilterListIcon />}
            >
              Filters
            </StyledFilterButton>
            <StyledFilterButton
              onClick={refreshLogs}
              disabled={loading}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </StyledFilterButton>
          </Stack>
        </Box>

        <ActivityLogPageFilterPanel
          showFilterPanel={showFilterPanel}
          filters={filters}
          tone={tone}
          initiatedBySearchResults={initiatedBySearchResults}
          associatedModelSearchResults={associatedModelSearchResults}
          linkedModelSearchResults={linkedModelSearchResults}
          initiatorTypeAnchorEl={initiatorTypeAnchorEl}
          associatedTypeAnchorEl={associatedTypeAnchorEl}
          linkedTypeAnchorEl={linkedTypeAnchorEl}
          onFilterChange={handleFilterChange}
          onGlobalSearchChange={handleGlobalSearchChange}
          onInitiatorTypeAnchorChange={setInitiatorTypeAnchorEl}
          onAssociatedTypeAnchorChange={setAssociatedTypeAnchorEl}
          onLinkedTypeAnchorChange={setLinkedTypeAnchorEl}
          onClearFilters={clearFilters}
        />
      </Box>

      <Box ref={scrollRootRef} sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && logs.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <ActivityLogEmptyState subtitle="There are no activity logs matching your current filters." />
        ) : (
          <Box>
            {logs.map((log, index) => (
              <div key={log.id} ref={index === logs.length - 1 ? lastLogElementRef : undefined}>
                <ActivityLogPageEntry
                  log={log}
                  tone={tone}
                  allLogs={logs}
                  backendService={backendService}
                />
              </div>
            ))}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </>
  );

  // Admins with View Client Exceptions get two tabs; everyone else sees the activity log as before.
  return (
    <StyledSection tone={tone} noTopPadding>
      <Box sx={{ pt: HEADING_SPACING.paddingTopFull }}>
        <PermissionGate permissionKey={Permissions.ViewClientExceptions} fallback={activity}>
          <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab value="activity" label="Activity" />
            <Tab
              value="client-exceptions"
              label="Client exceptions"
              data-testid="client-exceptions-tab"
            />
          </Tabs>
          {tab === 'activity' ? (
            activity
          ) : (
            <Suspense fallback={<CircularProgress />}>
              <ClientExceptionsPanel
                initialGroupId={queryGroupId}
                onSelectedGroupChange={handleSelectedClientExceptionChange}
              />
            </Suspense>
          )}
        </PermissionGate>
      </Box>
    </StyledSection>
  );
};

export default ActivityLogPage;
